import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { TextField, IconButton } from '@material-ui/core';
import Delete from '@material-ui/icons/Delete';

import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import ModalBase from 'components/Tvendo/Common/ModalBase';
import { wsUpdate } from 'services/WService';
import { onlyInteger } from 'hooks/Pipe';

const MAX_ROWS = 5;
const emptyRow = () => ({ fec_corta: '', cant_prod_corta: '' });

const isEmptyRow = (row) =>
  (!row.fec_corta || row.fec_corta === '') &&
  (row.cant_prod_corta === '' || row.cant_prod_corta === null || row.cant_prod_corta === undefined);

const ensureTrailingEmpty = (rows) => {
  const filled = rows.filter((row) => !isEmptyRow(row));
  if (filled.length < MAX_ROWS) filled.push(emptyRow());
  if (filled.length === 0) filled.push(emptyRow());
  return filled;
};

export default function ModalExpirations(props) {
  const { model, setOpen, inventoryId, entity, showNotify, onSaved, disabled } = props;
  const product = model?.product;
  const [rows, setRows] = useState([emptyRow()]);

  useEffect(() => {
    if (!model?.open) return;
    const initial = (product?.expirations || []).map((exp) => ({
      fec_corta: exp.fec_corta || '',
      cant_prod_corta: exp.cant_prod_corta ?? '',
    }));
    setRows(ensureTrailingEmpty(initial));
  }, [model?.open, product?.id]);

  const onHand = parseInt(product?.OnHand ?? 0, 10) || 0;
  const sum = rows.reduce((acc, row) => acc + (parseInt(row.cant_prod_corta, 10) || 0), 0);

  const handleRowChange = (index, field, value) => {
    const clone = rows.map((row, i) => (i === index ? { ...row, [field]: value } : row));
    setRows(ensureTrailingEmpty(clone));
  };

  const handleRemove = (index) => {
    const clone = rows.filter((_, i) => i !== index);
    setRows(ensureTrailingEmpty(clone));
  };

  const handleSave = async () => {
    const filled = rows
      .map((row) => ({
        fec_corta: (row.fec_corta || '').trim(),
        cant_prod_corta: row.cant_prod_corta,
      }))
      .filter((row) => !isEmptyRow(row));

    for (const row of filled) {
      const qty = parseInt(row.cant_prod_corta, 10);
      if (!row.fec_corta || isNaN(qty) || qty < 0) {
        showNotify('Cada vencimiento necesita fecha y una cantidad válida (≥ 0)', 'error');
        throw new Error('validation');
      }
    }

    if (sum > onHand) {
      showNotify(`La suma de cantidades (${sum}) supera el stock (${onHand})`, 'error');
      throw new Error('validation');
    }

    const payload = {
      expirations: filled.map((row) => ({
        fec_corta: row.fec_corta,
        cant_prod_corta: parseInt(row.cant_prod_corta, 10),
      })),
    };

    const response = await wsUpdate(
      `${entity}/${inventoryId}/detail/${product.id}/expirations`,
      payload
    );
    if (!response.success) {
      showNotify(response.message, 'error');
      throw new Error('save failed');
    }

    showNotify(response.message);
    const saved = (response.data || payload.expirations).map((exp) => ({
      id: exp.id,
      fec_corta: exp.fec_corta,
      cant_prod_corta: exp.cant_prod_corta,
    }));
    if (onSaved) onSaved(product.id, saved);
  };

  return (
    <ModalBase
      title={`Vencimientos${product?.ItemName ? ` · ${product.ItemName}` : ''}`}
      model={{ open: !!model?.open }}
      setOpen={setOpen}
      onSubmit={handleSave}
      showNotify={showNotify}
      disabledSave={disabled}
      fullWidth
      maxWidth="sm"
      noValidate
    >
      <GridContainer style={{ minWidth: '420px' }}>
        <GridItem xs={12}>
          <p style={{ margin: '0 0 8px', fontSize: '.85em' }}>
            Stock (OnHand): <b>{onHand}</b> · Suma cantidades:{' '}
            <b style={{ color: sum > onHand ? '#f44336' : 'inherit' }}>{sum}</b>
            {` / ${onHand}`}
          </p>
        </GridItem>
        <GridItem xs={6}><small><b>Fecha corta</b></small></GridItem>
        <GridItem xs={5}><small><b>Cantidad</b></small></GridItem>
        <GridItem xs={1} />
        {rows.map((row, index) => (
          <React.Fragment key={index}>
            <GridItem xs={6}>
              <TextField
                fullWidth
                type="date"
                margin="dense"
                value={row.fec_corta || ''}
                onChange={(e) => handleRowChange(index, 'fec_corta', e.target.value)}
                InputProps={{
                  inputProps: {
                    min: moment().format('YYYY-MM-DD'),
                    max: moment().add(5, 'months').format('YYYY-MM-DD'),
                  },
                }}
                disabled={disabled}
              />
            </GridItem>
            <GridItem xs={5}>
              <TextField
                fullWidth
                type="number"
                margin="dense"
                value={row.cant_prod_corta ?? ''}
                onChange={(e) => {
                  let value = parseInt(e.target.value, 10);
                  if (isNaN(value)) value = '';
                  else if (value < 0) value = 0;
                  handleRowChange(index, 'cant_prod_corta', value);
                }}
                onKeyPress={onlyInteger}
                inputProps={{ min: 0 }}
                disabled={disabled}
              />
            </GridItem>
            <GridItem xs={1} style={{ display: 'flex', alignItems: 'center' }}>
              {!isEmptyRow(row) && !disabled && (
                <IconButton size="small" onClick={() => handleRemove(index)} title="Quitar">
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </GridItem>
          </React.Fragment>
        ))}
        <GridItem xs={12}>
          <small style={{ color: '#999' }}>Máximo {MAX_ROWS} registros por producto.</small>
        </GridItem>
      </GridContainer>
    </ModalBase>
  );
}
